import React, { useRef } from 'react';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import { usePlaylistItemCallback } from '@jwp/ott-hooks-react/src/usePlaylistItemCallback';
import { useAdSchedule } from '@jwp/ott-hooks-react/src/useAdSchedule';
import useProtectedMedia from '@jwp/ott-hooks-react/src/useProtectedMedia';

import Player from '../../components/Player/Player';
import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';
import PlayerError, { PlayerErrorState } from '../../components/PlayerError/PlayerError';
import type { PlayerHandle } from '../../types/player';
import { useWatchHistoryListener } from '../../hooks/useWatchHistoryListener';

type Props = {
  item: PlaylistItem;
  seriesItem?: PlaylistItem;
  onPlay?: () => void;
  onPause?: () => void;
  onComplete?: () => void;
  onClose?: () => void;
  onUserActive?: () => void;
  onUserInActive?: () => void;
  onNext?: () => void;
  onBackClick?: () => void;
  feedId?: string;
  liveStartDateTime?: string | null;
  liveEndDateTime?: string | null;
  liveFromBeginning?: boolean;
  autostart?: boolean;
};

const PlayerContainer: React.FC<Props> = ({
  item,
  seriesItem,
  feedId,
  onPlay,
  onPause,
  onComplete,
  onUserActive,
  onUserInActive,
  onNext,
  onBackClick,
  liveEndDateTime,
  liveStartDateTime,
  autostart,
}: Props) => {
  // data
  const { data: adsData, isLoading: isAdsLoading } = useAdSchedule({ mediaId: item?.mediaid });
  const { playableItem, startOffset, isLoading, error } = useProtectedMedia(item);
  const { saveWatchProgress, handleTimeUpdate } = useWatchHistoryListener(item, seriesItem);

  // player events
  const playerHandle = useRef<PlayerHandle>(null);

  const handleTimeEvent = (params: { position: number; duration: number }) => {
    handleTimeUpdate(params);
  };

  const handlePlay = () => {
    saveWatchProgress();
    onPlay?.();
  };

  const handlePause = () => {
    saveWatchProgress();
    onPause?.();
  };

  const handleComplete = () => {
    onComplete?.();
  };

  const handleRemove = () => {
    saveWatchProgress();
  };

  const handleBackClick = () => {
    saveWatchProgress();
    onBackClick?.();
  };

  const handlePlaylistItemCallback = usePlaylistItemCallback(liveStartDateTime, liveEndDateTime);

  if (isLoading || isAdsLoading) {
    return <LoadingOverlay inline />;
  }

  if (!playableItem || error instanceof Error) {
    let playerError = PlayerErrorState.UNKNOWN;

    if (error instanceof Error) {
      if (error.message.toLowerCase() === 'access blocked') playerError = PlayerErrorState.GEO_BLOCKED;
      if (error.message.toLowerCase() === 'too many sessions') playerError = PlayerErrorState.TOO_MANY_SESSIONS;
      if (error.message.toLowerCase() === 'unauthorized') playerError = PlayerErrorState.UNAUTHORIZED;
    }

    return <PlayerError error={playerError} />;
  }

  return (
    <Player
      feedId={feedId}
      item={playableItem}
      adsData={adsData}
      on={{
        play: handlePlay,
        pause: handlePause,
        time: handleTimeEvent,
        complete: handleComplete,
        userActive: onUserActive,
        userInactive: onUserInActive,
        remove: handleRemove,
        next: onNext,
        backClick: handleBackClick,
      }}
      onPlaylistItemCallback={handlePlaylistItemCallback}
      startTime={startOffset}
      autostart={autostart}
      playerHandle={playerHandle}
    />
  );
};

export default PlayerContainer;
